import React, { useState, useEffect, useMemo } from "react";
import useApi from "../../hook/useApi";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import SearchableSelect from "../Common/SearchableSelect";

export default function AdminTradePanel({
  show,
  symbolData,
  scriptInfo,
  clients,
  initialSide = "BUY",
  onClose,
  onSubmit,
}) {
  const [side, setSide] = useState(initialSide || "BUY");
  const [orderType, setOrderType] = useState("Market");
  const [lot, setLot] = useState(1);
  const [qty, setQty] = useState(scriptInfo?.lotSize || 1);
  const [price, setPrice] = useState(symbolData?.ltp || 0);
  const [stopLoss, setStopLoss] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [recentTrades, setRecentTrades] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const { AsyncPostApiCall } = useApi();

  const fetchRecentTrades = async () => {
    try {
      const res = await AsyncPostApiCall("/orders/recent", {});
      if (res?.success) {
        setRecentTrades(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching recent trades", err);
    }
  };

  useEffect(() => {
    fetchRecentTrades();
  }, []);

  const isBuy = side === "BUY";

  // Initialize based on segment standards, but allow both to be edited
  useEffect(() => {
    const seg = (symbolData?.segment || "").toUpperCase();
    const lSize = scriptInfo?.lotSize || 1;
    if (
      seg.includes("NSE") ||
      seg.includes("BSE") ||
      seg.includes("CRYPTO") ||
      seg.includes("EQUITY")
    ) {
      setQty(1);
      setLot(1 / lSize);
    } else {
      setLot(1);
      setQty(lSize);
    }
  }, [symbolData?.segment, scriptInfo?.lotSize]);

  useEffect(() => {
    if (initialSide) setSide(initialSide);
  }, [initialSide]);

  useEffect(() => {
    // Correct Market Pricing: BUY at ASK, SELL at BID
    if (orderType === "Market") {
      const marketPrice = isBuy ? symbolData?.ask : symbolData?.bid;
      // Fallback to LTP if spread data is missing
      const finalPrice = marketPrice || symbolData?.ltp || 0;
      if (finalPrice) setPrice(finalPrice);
    }
  }, [symbolData?.ltp, symbolData?.ask, symbolData?.bid, orderType, isBuy]);

  const handleLotChange = (val) => {
    const lotSize = scriptInfo?.lotSize || 1;
    setLot(val);
    const parsedLot = parseFloat(val);
    if (!isNaN(parsedLot)) {
      setQty(parsedLot * lotSize);
    }
  };

  const handleQtyChange = (val) => {
    const lotSize = scriptInfo?.lotSize || 1;
    setQty(val);
    const parsedQty = parseFloat(val);
    if (!isNaN(parsedQty)) {
      setLot(parsedQty / lotSize);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      return toast.error("Please select a client to proceed with the trade.");
    }

    try {
      // Logic for side pricing: BUY at ASK, SELL at BID
      const marketPrice = isBuy ? symbolData?.ask : symbolData?.bid;
      const executionPrice =
        orderType === "Market" ? marketPrice || symbolData?.ltp : price;

      // === Price Validation Rules (same as user panel) ===
      const liveRef = isBuy
        ? symbolData?.ask || symbolData?.ltp || 0
        : symbolData?.bid || symbolData?.ltp || 0;
      if (orderType === "Limit") {
        if (isBuy && Number(price) > liveRef) {
          toast.error(
            `Buy LIMIT price (${price}) cannot be higher than live ASK (${liveRef})`,
          );
          return;
        }
        if (!isBuy && Number(price) < liveRef) {
          toast.error(
            `Sell LIMIT price (${price}) cannot be lower than live BID (${liveRef})`,
          );
          return;
        }
      } else if (orderType === "Stop") {
        if (isBuy && Number(price) <= liveRef) {
          toast.error(
            `Buy STOP price (${price}) must be strictly greater than live ASK (${liveRef})`,
          );
          return;
        }
        if (!isBuy && Number(price) >= liveRef) {
          toast.error(
            `Sell STOP price (${price}) must be strictly lower than live BID (${liveRef})`,
          );
          return;
        }
      }

      const requiresExpiry = ["NFO", "MCX", "CDS", "COMEX"].some((s) =>
        (symbolData?.segment || "").toUpperCase().includes(s),
      );

      const payload = {
        userId: selectedClient,
        symbol: symbolData.symbol,
        script: symbolData.name || symbolData.symbol.split(" ")[0],
        exchange: symbolData.exchange,
        segment: symbolData.segment,
        expiry: requiresExpiry ? symbolData.expiry || null : null,
        strike: requiresExpiry ? symbolData.strike || null : null,
        optionType: requiresExpiry ? symbolData.optionType || null : null,
        orderType: orderType.toUpperCase(),
        side,
        lotSize: scriptInfo?.lotSize || 1,
        quantity: qty,
        inputLot: lot,
        orderPrice: executionPrice,
        stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
        targetPrice: targetPrice ? parseFloat(targetPrice) : undefined,
      };

      let res;
      if (onSubmit) {
        res = await onSubmit(payload);
      } else {
        res = await AsyncPostApiCall("/orders/trade", payload);
      }

      if (res?.success) {
        // Parent might show its own toast, but we can ensure one is shown or just close
        onClose();
      }
    } catch (err) {
      console.error("Trade error", err);
    }

    // Refresh history
    setTimeout(() => fetchRecentTrades(), 1000);
  };

  const handleCancel = async (tradeId) => {
    const { isConfirmed } = await Swal.fire({
      title: "Cancel Order?",
      text: "Are you sure you want to CANCEL this pending order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Cancel it!",
      customClass: { popup: "swal-sm" },
    });

    if (isConfirmed) {
      const res = await AsyncPostApiCall(`/orders/admin-cancel/${tradeId}`, {});
      if (res?.success) {
        toast.success(res.message || "Order cancelled successfully");
        fetchRecentTrades();
      } else {
        toast.error(res?.message || "Failed to cancel order");
      }
    }
  };

  const fmt = (v, d = 2) => {
    if (v == null || isNaN(v)) return 0;
    return Number(v).toFixed(v < 10 && v > 0 ? 4 : d);
  };

  // The symbol title: exact symbol from socket or script string
  // The symbol title: exact symbol from socket or script string
  const formattedSymbolStr = [
    symbolData?.name || symbolData?.symbol,
    symbolData?.segment,
    symbolData?.expiry,
  ]
    .filter(Boolean)
    .join("_");

  const stats = [
    { label: "BID RATE", val: fmt(symbolData?.bid) },
    { label: "ASK RATE", val: fmt(symbolData?.ask) },
    { label: "LTP", val: fmt(symbolData?.ltp) },
    { label: "CHG", val: fmt(symbolData?.chp) + "%" },
    { label: "NET CHG", val: fmt(symbolData?.ch) },
    { label: "HIGH", val: fmt(symbolData?.high) },
    { label: "LOW", val: fmt(symbolData?.low) },
    { label: "OPEN", val: fmt(symbolData?.open) },
    { label: "CLOSE", val: fmt(symbolData?.close) },
  ];

  const orderTypeOptions = [
    { value: "Market", label: "Market" },
    { value: "Limit", label: "Limit" },
    { value: "Stop", label: "Stop" },
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      contentClassName={`admin-trade-panel ${isBuy ? "admin-trade-panel--buy" : "admin-trade-panel--sell"} border-0`}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <div className="">
        {/* Top Row: Symbol Name & Side selection */}
        <div className="admin-trade-header">
          <div className="admin-trade-symbol">{formattedSymbolStr}</div>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check admin-trade-radio">
              <input
                className="form-check-input"
                type="radio"
                name="tradeSide"
                id="tradeSell"
                checked={!isBuy}
                onChange={() => setSide("SELL")}
              />
              <label className="form-check-label fw-bold" htmlFor="tradeSell">
                Sell
              </label>
            </div>
            <div className="form-check admin-trade-radio">
              <input
                className="form-check-input"
                type="radio"
                name="tradeSide"
                id="tradeBuy"
                checked={isBuy}
                onChange={() => setSide("BUY")}
              />
              <label className="form-check-label fw-bold" htmlFor="tradeBuy">
                Buy
              </label>
            </div>
          </div>
        </div>

        {/* Stats Blocks */}
        <div className="admin-trade-stats mb-3">
          {stats.map((s, idx) => (
            <div key={idx} className="admin-trade-stat-card">
              <div className="admin-trade-stat-label">{s.label}</div>
              <div className="admin-trade-stat-value">{s.val}</div>
            </div>
          ))}
        </div>

        {/* Forms */}
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-2 col-md-2">
            <label className="form-label admin-trade-label mb-1">
              Order Type
            </label>
            <SearchableSelect
              placeholder="Select Order Type"
              className="admin-trade-input fw-bold"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
              options={orderTypeOptions.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
            />
          </div>

          <div className="col-md-2 col-md-1">
            <label className="form-label admin-trade-label mb-1">Lot</label>
            <input
              type="number"
              step="any"
              className="form-control form-control-sm admin-trade-input fw-bold"
              value={lot}
              onChange={(e) => handleLotChange(e.target.value)}
            />
          </div>

          <div className="col-md-2 col-md-1">
            <label className="form-label admin-trade-label mb-1">Qty</label>
            <input
              type="number"
              step="any"
              className="form-control form-control-sm admin-trade-input fw-bold"
              value={qty}
              onChange={(e) => handleQtyChange(e.target.value)}
            />
          </div>

          <div className="col-md-3 col-md-2">
            <label className="form-label admin-trade-label mb-1">Price</label>
            <input
              type="number"
              className={`form-control form-control-sm admin-trade-input fw-bold ${orderType === "Market" ? "bg-light text-muted opacity-75" : ""}`}
              style={{
                backgroundColor:
                  orderType === "Market"
                    ? undefined
                    : "var(--panel-input-bg, #fff)",
              }}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={orderType === "Market"}
            />
          </div>

          {/* <div className="col-6 col-md-2">
          <label
            className="form-label text-white fw-bold mb-1"
            style={{ fontSize: "11px", letterSpacing: "0.5px" }}
          >
            S/L
          </label>
          <input
            type="number"
            placeholder="Stop Loss"
            className="form-control form-control-sm border-0 radius-4 fw-bold text-dark"
            style={{ backgroundColor: "#ffffff" }}
            value={stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
          />
        </div>

        <div className="col-6 col-md-2">
          <label
            className="form-label text-white fw-bold mb-1"
            style={{ fontSize: "11px", letterSpacing: "0.5px" }}
          >
            Target
          </label>
          <input
            type="number"
            placeholder="Target"
            className="form-control form-control-sm border-0 radius-4 fw-bold text-dark"
            style={{ backgroundColor: "#ffffff" }}
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
          />
        </div> */}

          <div className="col-md-3 col-md-2">
            <label className="form-label admin-trade-label mb-1">Client</label>
            <SearchableSelect
              placeholder="Select Client"
              className="admin-trade-input fw-bold"
              value={selectedClient}
              onChange={(e) => {
                console.log("Selected client", e.target);
                setSelectedClient(e.target.value);
              }}
              options={clients.map((s) => ({
                label: `${s.loginId} - ${s.name}`,
                value: s._id,
              }))}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-success btn-sm fw-bold shadow-sm admin-trade-btn"
            onClick={handleSubmit}
          >
            OPEN NEW TRADE ORDER
          </button>
          <button
            className="btn btn-danger btn-sm fw-bold shadow-sm admin-trade-btn"
            onClick={onClose}
          >
            CANCEL
          </button>
          {recentTrades.length > 0 && (
            <button
              className="btn btn-dark btn-sm fw-bold shadow-sm ms-auto admin-trade-btn"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? "HIDE HISTORY" : "VIEW RECENT TRADES"}
            </button>
          )}
        </div>

        {/* Recent Trades Table (Collapsible) */}
        {recentTrades.length > 0 && showHistory && (
          <div className="mt-3 overflow-auto animate__animated animate__fadeInDown admin-trade-history">
            <div className="admin-trade-history-title">Recent Admin Trades</div>
            <table className="table table-sm table-hover mb-0 admin-trade-history-table">
              <thead>
                <tr className="text-uppercase">
                  <th>Client</th>
                  <th>Time</th>
                  <th>Side</th>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Price</th>
                  {/* <th>S/L</th>
                <th>Target</th> */}
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentTrades.map((t) => (
                  <tr key={t._id}>
                    <td>{t.userId?.loginId || "-"}</td>
                    <td>
                      {new Date(t.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td
                      className={t.side === "BUY" ? "text-info" : "text-danger"}
                    >
                      {t.side}
                    </td>
                    <td>{t.symbol}</td>
                    <td>{t.quantity}</td>
                    <td>{t.orderPrice}</td>
                    {/* <td>{t.stopLoss || "-"}</td>
                  <td>{t.targetPrice || "-"}</td> */}
                    <td>
                      <span
                        className={`badge bg-opacity-25 ${
                          t.status === "EXECUTED" || t.status === "CLOSED"
                            ? "bg-success text-success"
                            : t.status === "PENDING"
                              ? "bg-warning text-warning"
                              : t.status === "CANCELLED"
                                ? "bg-danger text-danger"
                                : "bg-secondary text-secondary"
                        }`}
                        style={{ fontSize: "10px" }}
                      >
                        {t.status === "PENDING" &&
                        (t.orderType === "LIMIT" || t.orderType === "STOP")
                          ? t.orderType
                          : t.status}
                      </span>
                    </td>
                    <td>
                      {t.status === "PENDING" && (
                        <button
                          className="btn btn-xs btn-outline-danger p-1 radius-4 d-flex align-items-center gap-1"
                          onClick={() => handleCancel(t._id)}
                          title="Cancel Order"
                          style={{ padding: "2px 5px", fontSize: "10px" }}
                        >
                          <Icon
                            icon="mdi:close-circle-outline"
                            style={{ fontSize: "12px" }}
                          />
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
